import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

type Tone = 'light' | 'dark'

interface EyebrowProps {
    children: ReactNode
    index?: string
    tone?: Tone
    className?: string
}

interface SectionHeadingProps {
    title: ReactNode
    titleId?: string
    eyebrow?: string
    index?: string
    lead?: ReactNode
    tone?: Tone
    align?: 'left' | 'center'
    as?: 'h1' | 'h2'
    className?: string
    titleClassName?: string
    leadClassName?: string
}

export const Eyebrow: FC<EyebrowProps> = ({ children, index, tone = 'light', className }): ReactNode => {
    return (
        <p
            className={cn(
                'flex items-center gap-3 text-[11.5px] font-medium uppercase tracking-[0.2em]',
                tone === 'light' ? 'text-ink-soft' : 'text-gold',
                className
            )}
        >
            {index && (
                <span className='tabular-nums'>
                    {index}
                </span>
            )}

            <span aria-hidden='true' className={cn('h-px w-8', tone === 'light' ? 'bg-terracotta' : 'bg-gold/70')} />

            <span>
                {children}
            </span>
        </p>
    )
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    title,
    titleId,
    eyebrow,
    index,
    lead,
    tone = 'light',
    align = 'left',
    as: Heading = 'h2',
    className,
    titleClassName,
    leadClassName
}): ReactNode => {
    return (
        <div className={cn(align === 'center' && 'flex flex-col items-center text-center', className)}>
            {eyebrow && (
                <Eyebrow index={index} tone={tone} className={cn('mb-6', align === 'center' && 'justify-center')}>
                    {eyebrow}
                </Eyebrow>
            )}

            <Heading
                id={titleId}
                className={cn(
                    'font-heading text-[2.6rem] leading-[1.02] tracking-[-0.015em] sm:text-[3.25rem] lg:text-[4.25rem]',
                    tone === 'light' ? 'text-ink' : 'text-ivory',
                    titleClassName
                )}
            >
                {title}
            </Heading>

            {lead && (
                <p
                    className={cn(
                        'mt-6 max-w-xl text-[16.5px] leading-relaxed',
                        tone === 'light' ? 'text-ink-soft' : 'text-ivory/70',
                        leadClassName
                    )}
                >
                    {lead}
                </p>
            )}
        </div>
    )
}
